import { CmpStr } from "cmpstr";
import { useCallback, useMemo } from "react";

/**
 * Available string similarity metrics
 */
export type SimilarityMetric = "jaroWinkler" | "levenshtein" | "dice";

/**
 * Hook for comparing string similarity using various algorithms
 * Default: Jaro-Winkler algorithm (good for short strings like names/titles)
 *
 * @param metric - The similarity algorithm to use (default: "jaroWinkler")
 * @returns Object with compareTwoStrings function
 *
 * @example
 * const { compareTwoStrings } = useStringSimilarity();
 * const similarity = compareTwoStrings("Hello World", "Hello World!"); // Returns ~0.95
 */
export default function useStringSimilarity(metric: SimilarityMetric = "jaroWinkler") {
  const cmp = useMemo(
    () => CmpStr.create().setMetric(metric).setFlags("i"), // "i" = case-insensitive
    [metric],
  );

  /**
   * Compare two strings and return a similarity score
   * @param source - First string to compare
   * @param target - Second string to compare
   * @returns Similarity score between 0 (no match) and 1 (exact match)
   */
  const compareTwoStrings = useCallback((source: string, target: string): number => {
    if (!source || !target)
      return 0;
    return cmp.compare(source, target);
  }, [cmp]);

  return { compareTwoStrings };
}
